/**
 * Order money formatting — currency-aware (orders may settle in non-USD),
 * building on the shared cents convention. Amounts are in cents on the wire.
 */
// A cached null marks a code Intl rejected (RangeError on a malformed or unrecognized currency),
// which keeps the warning to once per code per session. Caching a *USD* formatter under the bad
// key instead would be worse than not caching: it relabels the amount, permanently.
const formatters = new Map<string, Intl.NumberFormat | null>();

const formatterFor = (currency: string): Intl.NumberFormat | null => {
  if (!formatters.has(currency)) {
    try {
      formatters.set(currency, new Intl.NumberFormat('en-US', { style: 'currency', currency }));
    } catch (error) {
      console.warn(`Unsupported order currency code '${currency}'; showing the raw code instead:`, error);
      formatters.set(currency, null);
    }
  }
  return formatters.get(currency) ?? null;
};

/**
 * Format a cents amount in the order's transaction currency (default USD).
 * An unsupported code renders as `<amount> <CODE>` rather than being coerced to
 * USD — a partner reconciling an order must not be shown the wrong currency.
 */
export const formatMoney = (
  cents: number | null | undefined,
  currency: string | null | undefined = 'USD'
): string => {
  if (cents == null) return '—';
  // `|| 'USD'` and not the default parameter: transactionCurrencyCode is
  // nullable, and a default only fires for undefined.
  const code = currency || 'USD';
  const amount = cents / 100;
  return formatterFor(code)?.format(amount) ?? `${amount.toFixed(2)} ${code}`;
};

/**
 * Shopify tax lines arrive as decimal *dollars* in a string, unlike every other money field on
 * the order, which is integer cents. Convert before formatting — passing the raw value to
 * `formatMoney` divides it by 100 a second time and renders "8.25" as $0.08.
 */
export const taxLineCents = (price: string | null | undefined): number | null => {
  if (price == null || price === '') return null;
  const dollars = parseFloat(price);
  return Number.isNaN(dollars) ? null : Math.round(dollars * 100);
};

/**
 * `ccLast4` is an `Int` in the schema, so a card ending 0042 arrives as the number 42. Pad it
 * back out rather than rendering "···· 42", which a partner cannot match against a statement.
 */
export const formatCardLastFour = (last4: number | null | undefined): string =>
  (last4 == null ? '••••' : String(last4).padStart(4, '0'));
